/**
 * Permission Help Drawer
 * Persistent help accessible from settings/profile
 */

import React, { useState } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useMediaPermissions } from 'src/customHooks/mediaPermissions'
import { PermissionStatusIndicator } from './PermissionStatusIndicator'
import { BrowserSpecificHelp } from './BrowserSpecificHelp'
import { generateRecoveryPlan, exportEducationData } from 'src/services/mediaPermissions'

export const PermissionHelpDrawer = ({ open, onClose, anchor = 'right' }) => {
  const {
    cameraPermission,
    microphonePermission,
    browserInfo,
    checkPermissions,
    isChecking,
    permissionHistory,
  } = useMediaPermissions({ autoCheck: true })

  const [expanded, setExpanded] = useState('overview')

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  const recoveryPlan = generateRecoveryPlan('both', true)

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500 },
          maxWidth: '100vw',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Camera & Microphone Help
          </Typography>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {/* Current Status */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Current Status
            </Typography>
            <PermissionStatusIndicator
              cameraPermission={cameraPermission}
              microphonePermission={microphonePermission}
              showLabels={true}
              size="medium"
            />
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={checkPermissions}
              disabled={isChecking}
              sx={{ mt: 2 }}
            >
              {isChecking ? 'Checking...' : 'Refresh Status'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Accordions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Overview */}
            <Accordion
              expanded={expanded === 'overview'}
              onChange={handleAccordionChange('overview')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Why We Need Permissions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                  To participate in video calls and consultations, we need access to your camera
                  and microphone. This allows you to:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <li>See and hear your practitioner</li>
                  <li>Have face-to-face conversations</li>
                  <li>Share your video and audio</li>
                  <li>Get the most from your sessions</li>
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                  You have full control and can turn off your camera or microphone at any time
                  during a call.
                </Alert>
              </AccordionDetails>
            </Accordion>

            {/* How to Enable */}
            <Accordion
              expanded={expanded === 'enable'}
              onChange={handleAccordionChange('enable')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">How to Enable Permissions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <BrowserSpecificHelp recoveryPlan={recoveryPlan} showVisuals={false} />
              </AccordionDetails>
            </Accordion>

            {/* Browser Info */}
            <Accordion
              expanded={expanded === 'browser'}
              onChange={handleAccordionChange('browser')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Your Browser Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Browser:</strong> {browserInfo.browser || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Operating System:</strong> {browserInfo.os || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Device Type:</strong> {browserInfo.deviceType || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>PWA Mode:</strong> {browserInfo.isPWA ? 'Yes' : 'No'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Secure Connection:</strong> {browserInfo.isSecure ? 'Yes' : 'No'}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Troubleshooting */}
            <Accordion
              expanded={expanded === 'troubleshooting'}
              onChange={handleAccordionChange('troubleshooting')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Troubleshooting</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Common Issues:
                </Typography>
                <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                  <li>
                    <Typography variant="body2">
                      <strong>Permission prompt doesn't appear:</strong> Try refreshing the page
                      or checking your browser settings manually.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      <strong>Camera/microphone not working:</strong> Make sure no other
                      application is using your camera or microphone.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      <strong>Changed my mind:</strong> You can always change permissions in your
                      browser settings.
                    </Typography>
                  </li>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Contact Support */}
            <Accordion
              expanded={expanded === 'support'}
              onChange={handleAccordionChange('support')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">Still Need Help?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                  If you're still having trouble with camera or microphone permissions, our
                  support team is here to help.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    window.open('/help-center', '_blank')
                  }}
                  sx={{
                    backgroundColor: '#514387',
                    '&:hover': {
                      backgroundColor: '#403266',
                    },
                  }}
                >
                  Contact Support
                </Button>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}

export default PermissionHelpDrawer


















